// Camada de dados dos simulados do SOS 2026.
//
// Questões, respostas, envio e gabarito vêm das rotas de simulationlandings
// do backend (api/backend/v2/simulatedlanding). Cada área aponta para um
// documento pelo `simulationSlug` (data/calendar.js), e o período em que ela
// fica no ar é o startDate/endDate desse documento.
//
// O login confere a inscrição em landingpagesregistrations (POST
// /dashboard/lp/check) e guarda a sessão no localStorage.

import config from "../../../core/config";
import { AREAS, getArea } from "../data/calendar";
import { LEAD_EVENT } from "../../sos_lp/content";

const SESSION_KEY = "sos-2026:aluno";
const LETTERS = ["A", "B", "C", "D", "E"];

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

// Não usa o http() do core: ele troca o `user` do body pelo inscrito do SOS 2023.
const post = async (path, body) => {
  const response = await fetch(`${config.apiUrl}/api/backend/v2/simulatedlanding/${path}`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.error) throw new Error(data.message || "Não foi possível falar com o servidor.");
  return data;
};

// Sessão ---------------------------------------------------------------

export const getSession = () => read(SESSION_KEY, null);

// Grava a sessão sem conferir nada (a inscrição da LP acabou de cadastrar).
// Sem nome, mantém o já salvo para o mesmo e-mail.
export const saveSession = ({ name, email, whatsapp }) => {
  const user = { email: email.trim().toLowerCase(), whatsapp: whatsapp.replace(/\D/g, "") };
  const current = getSession();
  const savedName = current && current.email === user.email ? current.name : null;
  if (name || savedName) user.name = (name || savedName).trim();
  write(SESSION_KEY, user);
  return user;
};

// Só entra quem tem inscrição no SOS com esse e-mail + WhatsApp.
export const login = async ({ email, whatsapp }) => {
  try {
    const response = await fetch(`${config.apiUrl}/api/backend/v2/dashboard/lp/check`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim().toLowerCase(), phone: whatsapp.replace(/\D/g, ""), event: LEAD_EVENT }),
    });
    if (response.status === 404) {
      return { ok: false, error: "Não encontramos uma inscrição com esses dados. Use o mesmo e-mail e WhatsApp da inscrição no SOS." };
    }
    if (!response.ok) return { ok: false, error: "Não deu pra entrar agora. Tenta de novo em instantes." };
    const { name } = await response.json();
    return { ok: true, user: saveSession({ name, email, whatsapp }) };
  } catch (e) {
    return { ok: false, error: "Não deu pra entrar agora. Confira a sua conexão e tenta de novo." };
  }
};

export const logout = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch (e) {}
};

// A tentativa no backend exige `name`, e o login do SOS só pede e-mail e WhatsApp.
const student = () => {
  const user = getSession();
  return user && { name: user.name || user.email, email: user.email, whatsapp: user.whatsapp };
};

// Simulados ------------------------------------------------------------
// Cache por área: { id, questions } do documento em simulationlandings.

const simulations = {};

const byOrder = (a, b) => (a.order || 0) - (b.order || 0);

const toQuestions = (simulated) =>
  [...(simulated.questions || [])].sort(byOrder).map((q) => ({
    id: String(q.questionId),
    statement: q.text,
    alternatives: q.alternatives.map((a, j) => ({ id: String(a.alternativeId), letter: LETTERS[j], text: a.text })),
  }));

// O verify de quem ainda não começou vem sem questões. Uma resposta assim
// (inclusive uma atrasada, de antes do start) não apaga as que já estão no cache.
const cache = (slug, simulated) => {
  const questions = toQuestions(simulated);
  const prev = simulations[slug];
  simulations[slug] = { id: simulated.id, questions: questions.length || !prev ? questions : prev.questions };
  return simulations[slug];
};

// Respostas do backend ({ question, answer } por id) viram { [indice]: "A".."E" }.
const toAnswers = (questions, history) => {
  const picked = {};
  (history.answers || []).forEach((a) => {
    if (a.answer) picked[a.question] = a.answer;
  });
  return questions.reduce((out, q, i) => {
    const alt = q.alternatives.find((a) => a.id === picked[q.id]);
    if (alt) out[i] = alt.letter;
    return out;
  }, {});
};

// Formato: { answers: { [indice]: "A".."E" }, startedAt, submittedAt, score }
const toProgress = (simulated, { questions }, history = {}) => ({
  answers: toAnswers(questions, history),
  startedAt: history.startDate || null,
  submittedAt: history.endDate || null,
  score: history.endDate ? (history.mathCorrectAnswersCount || 0) + (history.naturalSciencesCorrectAnswersCount || 0) : null,
});

const verify = async (area) => {
  const { simulated, history } = await post("verify/started", { simulated: area.simulationSlug, user: student() });
  return toProgress(simulated, cache(area.slug, simulated), history);
};

// Período --------------------------------------------------------------
// { [area]: { opensAt, closesAt } } com o startDate/endDate de cada simulado.
// Rota pública: vale também para quem ainda não entrou. Se falhar, as telas
// usam as datas do calendário.

export const getPeriods = async () => {
  try {
    const slugs = AREAS.map((a) => a.simulationSlug).join(",");
    const response = await fetch(`${config.apiUrl}/api/backend/v2/simulatedlanding/periods?slugs=${slugs}`);
    if (!response.ok) return {};
    const list = await response.json();
    return AREAS.reduce((all, area) => {
      const doc = list.find((it) => it.slug === area.simulationSlug);
      return doc ? { ...all, [area.slug]: { opensAt: doc.startDate, closesAt: doc.endDate } } : all;
    }, {});
  } catch (e) {
    return {};
  }
};

// Progresso ------------------------------------------------------------

export const getProgress = async () => {
  if (!student()) return {};
  const entries = await Promise.all(AREAS.map((area) => verify(area).then((p) => [area.slug, p], () => null)));
  return entries.filter(Boolean).reduce((all, [slug, p]) => ({ ...all, [slug]: p }), {});
};

export const startSimulado = async (slug) => {
  const { simulated, history } = await post("start", { simulated: getArea(slug).simulationSlug, user: student() });
  return toProgress(simulated, cache(slug, simulated), history);
};

// Recebe a questão que está na tela, não depende das questões do cache.
export const saveAnswer = async (slug, question, letter) => {
  if (!simulations[slug]) await verify(getArea(slug));
  const alt = question.alternatives.find((a) => a.letter === letter);
  await post("answer", { simulated: simulations[slug].id, question: question.id, answer: alt.id, user: student() });
};

// O backend recusa resposta fora do período e mantém uma tentativa só.
export const submitSimulado = async (slug) => {
  await post("finish", { simulated: simulations[slug].id, user: student() });
};

// Questões -------------------------------------------------------------
// Sem gabarito nem resolução: isso só vem em getGabarito, depois do envio
// (ou depois do fim do período).

export const getQuestions = async (slug) => {
  const area = getArea(slug);
  if (!area) return [];
  if (!simulations[slug] || !simulations[slug].questions.length) await verify(area);
  return simulations[slug].questions;
};

// O backend tira as tags do enunciado, mas deixa as entidades (&nbsp;, &aacute;).
// DOMParser não executa scripts, então serve só para decodificar o texto.
const decodeText = (text) =>
  new DOMParser()
    .parseFromString(text || "", "text/html")
    .documentElement.textContent.replace(/\s+/g, " ")
    .trim();

// Linhas do gabarito: { n, statement, right, mine, hit, resolution }.
export const getGabarito = async (slug) => {
  const { resolutions, hasAttempt } = await post(`${getArea(slug).simulationSlug}/result`, { user: student() });
  return [...resolutions].sort(byOrder).map((r, i) => ({
    n: i + 1,
    statement: decodeText(r.questionText),
    right: r.correctLetter,
    mine: hasAttempt ? r.myAnswer || null : null,
    hit: hasAttempt ? r.isCorrect : null,
    resolution: r.resolution,
  }));
};
